import React, { useCallback, useRef } from 'react';
import useClassNames from '@loomstateorg/shared-frontend/hooks/useClassNames';

const QuantityChevronUp: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M18.2929 15.2893C18.6834 14.8988 18.6834 14.2656 18.2929 13.8751L13.4007 8.98766C12.6195 8.20726 11.3537 8.20757 10.5729 8.98835L5.68257 13.8787C5.29205 14.2692 5.29205 14.9024 5.68257 15.2929C6.0731 15.6835 6.70626 15.6835 7.09679 15.2929L11.2824 11.1073C11.673 10.7168 12.3061 10.7168 12.6966 11.1073L16.8787 15.2893C17.2692 15.6798 17.9024 15.6798 18.2929 15.2893Z" />
  </svg>
);

const QuantityChevronDown: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M5.70711 9.71069C5.31658 10.1012 5.31658 10.7344 5.70711 11.1249L10.5993 16.0123C11.3805 16.7927 12.6463 16.7924 13.4271 16.0117L18.3174 11.1213C18.708 10.7308 18.708 10.0976 18.3174 9.70708C17.9269 9.31655 17.2937 9.31655 16.9032 9.70708L12.7176 13.8927C12.3271 14.2833 11.6939 14.2832 11.3034 13.8927L7.12132 9.71069C6.7308 9.32016 6.09763 9.32016 5.70711 9.71069Z" />
  </svg>
);

export type QuantityNumberInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'value'
> & {
  value: number | string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inputClassName?: string;
  hideSpinners?: boolean;
  onMaxHover?: () => void;
  onMaxLeave?: () => void;
};

const spinnerButtonClassName =
  'quantity-number-input-spinner pointer-events-auto z-10 flex h-1/2 items-center justify-center text-inherit';

const QuantityNumberInput: React.FC<QuantityNumberInputProps> = ({
  value,
  onChange,
  className = '',
  inputClassName = '',
  min = 0,
  max,
  disabled,
  step = 1,
  hideSpinners = false,
  onMaxHover,
  onMaxLeave,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const wrapperClassName = useClassNames([
    'quantity-number-input-wrapper relative w-full min-w-0 text-inherit',
    disabled ? 'cursor-default' : '',
    className,
  ]);

  const inputClassNameResolved = useClassNames([
    'quantity-number-input relative z-0 min-w-0 w-full border-0 bg-transparent text-inherit tabular-nums',
    'py-4 pl-4 pr-18 text-left xl:pr-20',
    'focus:border-0 focus:outline-none focus:ring-0',
    disabled ? 'cursor-default' : '',
    inputClassName,
  ]);

  const numericValue = parseFloat(String(value));
  const safeValue = Number.isNaN(numericValue) ? 0 : numericValue;
  const minValue = typeof min === 'number' ? min : 0;
  const stepValue = typeof step === 'number' ? step : parseFloat(String(step)) || 1;
  const isAtMin = safeValue <= minValue;
  const isAtMax = typeof max === 'number' && safeValue >= max;

  const emitChange = useCallback(
    (nextValue: number) => {
      const input = inputRef.current;
      if (!input) return;

      const clamped =
        typeof max === 'number' ? Math.min(max, Math.max(minValue, nextValue)) : Math.max(minValue, nextValue);

      const valueDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      valueDescriptor?.set?.call(input, String(clamped));

      onChange({
        target: input,
        currentTarget: input,
      } as React.ChangeEvent<HTMLInputElement>);
    },
    [max, minValue, onChange],
  );

  const handleStep = useCallback(
    (direction: 'up' | 'down') => {
      if (disabled) return;
      if (direction === 'up' && isAtMax) return;
      if (direction === 'down' && isAtMin) return;

      const nextValue = direction === 'up' ? safeValue + stepValue : safeValue - stepValue;
      emitChange(nextValue);
    },
    [disabled, emitChange, isAtMax, isAtMin, safeValue, stepValue],
  );

  const { ...rest } = props;

  return (
    <div
      className={wrapperClassName}
      data-testid="atom-quantity-number-input"
      onMouseEnter={hideSpinners && onMaxHover ? onMaxHover : undefined}
      onMouseLeave={hideSpinners && onMaxLeave ? onMaxLeave : undefined}
      onClick={hideSpinners && onMaxHover ? onMaxHover : undefined}
    >
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={inputClassNameResolved}
        {...rest}
        data-testid={(props as { 'data-testid'?: string })['data-testid'] ?? 'atom-quantity-number-input-field'}
      />
      {!hideSpinners && (
        <div className="quantity-number-input-spinners pointer-events-none absolute inset-y-0 right-0 z-10 flex w-18 flex-col justify-center xl:w-20">
          <button
            type="button"
            tabIndex={-1}
            aria-label="Increase quantity"
            data-testid="atom-quantity-number-input-increment"
            onClick={isAtMax && onMaxHover ? onMaxHover : () => handleStep('up')}
            onMouseEnter={isAtMax ? onMaxHover : undefined}
            onMouseLeave={isAtMax ? onMaxLeave : undefined}
            className={`${spinnerButtonClassName} items-end pb-0 xl:pb-1 ${disabled ? 'cursor-default' : isAtMax ? 'cursor-not-allowed' : ''}`}
          >
            <QuantityChevronUp className="size-20 shrink-0 fill-current" />
          </button>
          <button
            type="button"
            tabIndex={-1}
            aria-label="Decrease quantity"
            data-testid="atom-quantity-number-input-decrement"
            onClick={() => handleStep('down')}
            className={`${spinnerButtonClassName} items-start pt-0 xl:pt-1 ${disabled ? 'cursor-default' : isAtMin ? 'cursor-not-allowed' : ''}`}
          >
            <QuantityChevronDown className="size-20 shrink-0 fill-current" />
          </button>
        </div>
      )}
    </div>
  );
};

export default QuantityNumberInput;
